import type { LatLng, PathOptions } from 'leaflet';
import sharedAttackLine from './shared/attackLine';
import { graphicColor } from './sidc';

/**
 * Low-level wrapper around the MIL-STD-2525 multipoint renderer
 * (`@armyc2.c5isr.renderer/mil-sym-ts-web`, the maintained TypeScript successor
 * to mil-sym-js). milsymbol — used elsewhere for single-point unit/marker icons
 * — cannot draw multipoint tactical graphics (arrows, boundaries, phase lines);
 * this module fills that gap and is deliberately symbol-agnostic: it renders
 * whatever SIDC + control points it is handed. Symbol-specific knowledge (draw
 * rule, control-point ordering, default sizing) lives in the TacticalSymbol
 * subclasses, not here.
 *
 * The ~9 MB renderer bundle is loaded lazily via dynamic import() so it lands in
 * its own webpack chunk and is fetched only the first time a user draws a
 * graphic, never bloating the initial map load.
 */

/** Minimal surface of the renderer we actually use. */
interface WebRendererLike {
    OUTPUT_FORMAT_GEOJSON: number;
    RenderSymbol(
        id: string,
        name: string,
        description: string,
        symbolCode: string,
        controlPoints: string,
        altitudeMode: string,
        scale: number,
        bbox: string,
        modifiers: Map<string, string>,
        attributes: Map<string, string>,
        format: number,
    ): string;
}

export interface MilSym {
    WebRenderer: WebRendererLike;
    MilStdAttributes: Record<string, string>;
}

let milSymPromise: Promise<MilSym> | null = null;

/**
 * Lazily import and cache the renderer. Safe to call repeatedly — the dynamic
 * import resolves once and every caller shares the same module instance.
 */
export function loadMilSym(): Promise<MilSym> {
    if (!milSymPromise) {
        milSymPromise = import('@armyc2.c5isr.renderer/mil-sym-ts-web').then((m: any) => {
            // Named exports may sit on the namespace directly or under default,
            // depending on how the bundler interops the ESM package.
            const ns = m && m.WebRenderer ? m : (m && m.default) || m;
            return { WebRenderer: ns.WebRenderer, MilStdAttributes: ns.MilStdAttributes } as MilSym;
        });
    }
    return milSymPromise;
}

/**
 * Notional canvas width, in pixels, the renderer converts the symbol into.
 *
 * C5Ren rasterises control points into a pixel space derived from the bbox and
 * scale it is handed, then merges vertices that land close together. Pinning the
 * symbol to a fixed pixel budget guarantees the merge threshold is always small
 * relative to the graphic itself. 1200 sits mid-range of the renderer's own
 * 720–1920 px clamp (`MultiPointHandler._min/_maxPixelWidth`), so
 * `getReasonableScale()` passes our scale through untouched.
 */
const RENDER_CANVAS_PX = 1200;

/** Padding around the geometry's own extent, so the arrow is never flush with the bbox edge. */
const FRAME_PADDING = 1.25;

/**
 * Floor on the framed extent, in metres. A graphic drawn a few metres across
 * would otherwise derive a scale so fine that the renderer works in millions of
 * pixels for no visible gain.
 */
const MIN_FRAME_EXTENT_M = 100;

/** 96 px per inch ÷ 0.0254 m per inch — the renderer's own pixels-per-metre assumption. */
const PIXELS_PER_METER_AT_96DPI = 3779.527559;

/**
 * The bbox and scale to render a symbol against, derived from the symbol's own
 * control points.
 *
 * Deliberately **not** the map viewport. C5Ren re-derives its scale from
 * whatever bbox it is given (`MultiPointHandler.getReasonableScale`) and then
 * drops control points that collide in the resulting pixel space — so a viewport
 * bbox made the rendered geometry a function of the current zoom. Zoomed out,
 * intermediate vertices were silently discarded and a multi-segment axis of
 * advance degenerated into a straight tail-to-tip arrow, which then stayed that
 * way because the layer only re-renders when the collection changes.
 *
 * Framing on the geometry instead makes the output a pure function of the stored
 * shape — identical at every zoom and on every client, which is the guarantee
 * ADR-0023 makes for a shared graphic. Nothing is lost by not clipping to the
 * viewport: Leaflet already clips the resulting paths.
 *
 * The frame is square *in metres* so a north-south arrow is scaled the same as
 * an east-west one — the renderer measures only the bbox width.
 */
function symbolFrame(controlPoints: LatLng[]): { bbox: string; scale: number } {
    const lngs = controlPoints.map((p) => p.lng);
    const lats = controlPoints.map((p) => p.lat);
    const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const metersPerDegLon = sharedAttackLine.metersPerDegLon(midLat);

    const widthM = (Math.max(...lngs) - Math.min(...lngs)) * metersPerDegLon;
    const heightM = (Math.max(...lats) - Math.min(...lats)) * sharedAttackLine.M_PER_DEG_LAT;
    const extentM = Math.max(widthM, heightM, MIN_FRAME_EXTENT_M) * FRAME_PADDING;

    const midLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    const halfLng = extentM / 2 / metersPerDegLon;
    const halfLat = extentM / 2 / sharedAttackLine.M_PER_DEG_LAT;

    return {
        // "lowerLeftLon,lowerLeftLat,upperRightLon,upperRightLat"
        bbox: `${midLng - halfLng},${midLat - halfLat},${midLng + halfLng},${midLat + halfLat}`,
        scale: (extentM * PIXELS_PER_METER_AT_96DPI) / RENDER_CANVAS_PX,
    };
}

/**
 * Render a multipoint symbol to a GeoJSON FeatureCollection ready for
 * `L.geoJSON`. The order of `controlPointsLatLng` is dictated by the SIDC's draw
 * rule and is the caller's (the symbol's) responsibility. Returns null when
 * there are too few points or the renderer reports an error (it returns a
 * `{type:"error"}` object rather than throwing).
 *
 * The result depends only on the SIDC and the control points — see
 * {@link symbolFrame} for why the map is deliberately not consulted.
 */
export function renderMultipoint(
    milSym: MilSym,
    sidc: string,
    controlPointsLatLng: LatLng[],
): any | null {
    if (controlPointsLatLng.length < 2) return null;

    // Control points are "lon,lat" pairs (x,y) separated by spaces.
    const controlPoints = controlPointsLatLng.map((p) => `${p.lng},${p.lat}`).join(' ');
    const { bbox, scale } = symbolFrame(controlPointsLatLng);

    try {
        const out = milSym.WebRenderer.RenderSymbol(
            'ft-tactical',
            '',
            '',
            sidc,
            controlPoints,
            'clampToGround',
            scale,
            bbox,
            new Map<string, string>(),
            new Map<string, string>(),
            milSym.WebRenderer.OUTPUT_FORMAT_GEOJSON,
        );
        const parsed = JSON.parse(out);
        return parsed && parsed.type === 'FeatureCollection' ? parsed : null;
    } catch {
        return null;
    }
}

/**
 * Drop the renderer's trailing metadata feature (a Polygon with empty
 * coordinates carrying only symbol id/name), keeping the drawable geometry.
 */
export function hasDrawableGeometry(feature: any): boolean {
    const coords = feature && feature.geometry && feature.geometry.coordinates;
    return Array.isArray(coords) && coords.length > 0;
}

/**
 * Map the styling the renderer bakes into each GeoJSON feature onto Leaflet path
 * options. A polygon is only filled when the renderer supplies a fill colour;
 * otherwise it is stroked hollow (the arrow body outline is a fill-less polygon
 * and should not render as a solid blob).
 *
 * Colour comes from the graphic's own affiliation when `sidc` is supplied,
 * overriding the renderer's palette — the standard's friendly blue is one map
 * layer among many out in the field, where a black-ink overlay reads better; the
 * remaining affiliations keep their conventional hues. Everything *else* about
 * the feature (stroke width, dash pattern, whether the polygon is filled) still
 * comes from the renderer, so the Planned/Anticipated dashes an enemy graphic
 * carries are the standard's, not ours.
 */
export function tacticalFeatureStyle(feature: any, sidc?: string): PathOptions {
    const props = (feature && feature.properties) || {};
    const geomType = feature && feature.geometry && feature.geometry.type;
    const isPolygon = geomType === 'Polygon' || geomType === 'MultiPolygon';
    const color = sidc ? graphicColor(sidc) : props.strokeColor || '#000000';
    const filled = isPolygon && !!props.fillColor;
    return {
        color,
        weight: props.strokeWidth || 3,
        opacity: props.lineOpacity != null ? props.lineOpacity : 1,
        lineCap: 'square',
        // The renderer emits `strokeDasharray` as a number array; Leaflet wants
        // the SVG string. This is the *only* thing that makes a
        // Planned/Anticipated graphic look planned — drop it and an anticipated
        // enemy axis is indistinguishable from an observed one.
        dashArray: dashArrayOf(props),
        fill: filled,
        fillColor: color,
        fillOpacity: filled ? 1 : 0,
    };
}

/** The renderer's dash pattern as an SVG `stroke-dasharray` string, if any. */
function dashArrayOf(props: any): string | undefined {
    const dashes = props.strokeDasharray;
    if (Array.isArray(dashes)) return dashes.length ? dashes.join(' ') : undefined;
    return typeof dashes === 'string' && dashes ? dashes : undefined;
}
